import { FC, useCallback, useEffect, useMemo, useState } from "react";
import { PageHeader } from "@hostingbv/lib/src/components/content/PageHeader";
import { ContentContainer } from "@hostingbv/lib/src/components/content/ContentContainer";
import { Breadcrumbs } from "@hostingbv/lib/src/components/content/Breadcrumbs";
import { SectionHeader } from "@hostingbv/lib/src/components/content/SectionHeader";
import { Input } from "@hostingbv/lib/src/components/form/Input";
import { Autocomplete } from "@hostingbv/lib/src/components/form/Autocomplete";
import { PageIconHeader } from "@hostingbv/lib/src/components/PageIconHeader";
import { faRocket, faShop } from "@fortawesome/free-solid-svg-icons";
import { useCustomers, useOrderCertificate, useServers } from "@hostingbv/lib/src/api/partner/PartnerHooks";
import { Button } from "@hostingbv/lib/src/components/form/Button";
import { ChallengeType, KeyLength, KeyType } from "@hostingbv/lib/src/api/partner/models/Certificate";
import { Callout } from "@hostingbv/lib/src/components/content/Callout";
import { ValidationError } from "@hostingbv/lib/src/api/ModelFactory";
import { useNavigate, useSearchParams } from "react-router-dom";

export const CertificateCreate: FC = () => {
  const [searchParams] = useSearchParams()
  const [name, setName] = useState(() => searchParams.get('name') ?? '')
  const [customer, setCustomer] = useState('')
  const [server, setServer] = useState('')
  const [domains, setDomains] = useState(() => searchParams.get('domain') ?? '')
  const [challengeType, setChallengeType] = useState('http-01')
  const [keyType, setKeyType] = useState('ecdsa')
  const [keyLength, setKeyLength] = useState('256')

  const { data: servers } = useServers()
  const serverOptions = useMemo(() => Object.fromEntries(servers?.filter(x => x.serverType === 'windhosting_server').map(x => [x.id, x.displayName]) ?? []), [servers])

  const { data: customers } = useCustomers()
  const customerOptions = useMemo(() => Object.fromEntries(customers?.map(x => [x.id, x.displayName]) ?? []), [customers])

  useEffect(() => { if (!server) setServer(servers?.filter(x => x.serverType === 'windhosting_server')?.[0]?.id ?? '') }, [servers, server])
  useEffect(() => { if (!customer) setCustomer(customers?.[0]?.id ?? '') }, [customers, customer])
  useEffect(() => setKeyLength(keyType === 'ecdsa' ? '256' : '4096'), [keyType])

  const isSubmissionValid = name.length > 3 && customer !== "" && server !== "" && domains.trim().length > 0

  const navigate = useNavigate()
  const { mutate: orderCertificate, error: orderError } = useOrderCertificate()

  const order = useCallback(async () => {
    try {
      const certificate = await orderCertificate({
        type: 'acme_certificate',
        customer: customer,
        server: server,
        displayName: name,
        domains: domains.split(','),
        challengeType: challengeType as ChallengeType,
        keyType: keyType as KeyType,
        keyLength: keyLength as KeyLength
      })

      navigate('/certificates/' + certificate.id + "/finalize", { state: { certificate } })
    } catch {
      // error is displayed using orderError below
    }
  }, [customer, server, name, domains, challengeType, keyType, keyLength, orderCertificate, navigate])

  return (
    <ContentContainer>
      <PageIconHeader icon={faShop}>
        <Breadcrumbs crumbs={[
          { label: "Certificates", href: "/certificates" },
        ]} currentPage={"Add new certificate"} />
        <PageHeader>Add new certificate</PageHeader>
      </PageIconHeader>

      {orderError instanceof ValidationError ? <Callout color={"danger"}>
        {orderError.errors.length > 0
          ? <ul className="list-disc pl-5">{orderError.errors.map((e, i) => <li key={i}>{e}</li>)}</ul>
          : orderError.message}
      </Callout> : <></>}

      <SectionHeader>Certificate properties</SectionHeader>
      <section className={"space-y-4 mb-8"}>
        <Input type={"text"} label={"Name"} value={name} onChange={setName} placeholder={"blog.example.com (2026)"} />
        <Autocomplete label={"Customer"} value={customer} onChange={setCustomer} options={customerOptions} />
        <Autocomplete label={"Server"} value={server} onChange={setServer} options={serverOptions} />
        <Input type={"text"} label={"Domains"} value={domains} onChange={setDomains} placeholder={"blog.example.com,www.blog.example.com"} />
        <details>
          <summary className="text-sm font-medium text-brand-900">Advanced settings</summary>
          <div className="mt-4">
            <Autocomplete label={"ChallengeType"} value={challengeType} onChange={setChallengeType} options={{ "http-01": "HTTP", "dns-01": "DNS" }} />
            <Autocomplete label={"KeyType"} value={keyType} onChange={setKeyType} options={{ "rsa": "RSA", "ecdsa": "ECDSA" }} />
            <Autocomplete label={"KeyLength"} value={keyLength} onChange={setKeyLength} options={keyType === 'rsa' ? { "4096": "4096" } : { "256": "256", "384": "384", "512": "512" }} />
          </div>
        </details>
      </section>
      <Button type={'primary'} size={'sm'} disabled={!isSubmissionValid} onClick={order} icon={faRocket} text={'Create certificate'} />
    </ContentContainer>
  );
}
